import { useMemo } from 'react'
import { toast } from 'react-toastify'
import {
  CheckCircle2,
  XCircle,
  ChevronRight,
  Rocket,
  Pause,
  Loader2,
} from 'lucide-react'

import FbButton from 'components/FbUI/FbButton'

import {
  Sequence,
  useGetCampaignLeads,
  useGetCampaignSenderEmails,
  useLaunchCampaign,
  usePauseCampaign,
} from '../sequences_api'
import { useGetCampaignSchedule } from '../campaign_schedule_api'
import { useGetCampaignSettings } from '../campaign_settings_api'

// Campaign status constants
const STATUS = {
  DRAFT: 'draft',
  LAUNCHING: 'launching',
  QUEUED: 'queued',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
} as const

type SequenceSubTab =
  | 'steps'
  | 'settings'
  | 'schedule'
  | 'leads'
  | 'sender-emails'
  | 'launch'

interface LaunchTabProps {
  campaignId: number
  bisonCampaignId: number
  sequence: Sequence | null
  onNavigateToTab: (tab: SequenceSubTab) => void
}

interface ChecklistItem {
  id: string
  title: string
  isComplete: boolean
  description: string
  targetTab: SequenceSubTab
}

function ChecklistItemRow({
  item,
  onNavigate,
}: {
  item: ChecklistItem
  onNavigate: () => void
}) {
  return (
    <div className="flex items-center justify-between py-4 px-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-4">
        {/* Status icon */}
        <div className="flex-shrink-0">
          {item.isComplete ? (
            <CheckCircle2 className="w-6 h-6 text-green-500" />
          ) : (
            <XCircle className="w-6 h-6 text-red-400" />
          )}
        </div>
        {/* Text content */}
        <div>
          <div className="font-medium text-gray-900">{item.title}</div>
          <div
            className={`text-sm ${item.isComplete ? 'text-gray-500' : 'text-red-500'}`}
          >
            {item.description}
          </div>
        </div>
      </div>
      {/* Navigate button */}
      <button
        onClick={onNavigate}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
      >
        Go
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const getStatusStyle = () => {
    switch (status) {
      case STATUS.ACTIVE:
      case STATUS.LAUNCHING:
      case STATUS.QUEUED:
        return 'bg-green-100 text-green-800 border-green-200'
      case STATUS.PAUSED:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case STATUS.COMPLETED:
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case STATUS.DRAFT:
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusLabel = () => {
    switch (status) {
      case STATUS.ACTIVE:
        return 'Active'
      case STATUS.LAUNCHING:
      case STATUS.QUEUED:
        return 'Launching...'
      case STATUS.PAUSED:
        return 'Paused'
      case STATUS.COMPLETED:
        return 'Completed'
      case STATUS.DRAFT:
      default:
        return 'Draft'
    }
  }

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyle()}`}
    >
      {getStatusLabel()}
    </span>
  )
}

export function LaunchTab({
  campaignId,
  bisonCampaignId,
  sequence,
  onNavigateToTab,
}: LaunchTabProps) {
  // Fetch all required data for checklist
  const { data: settings } = useGetCampaignSettings(campaignId)
  const { data: schedule } = useGetCampaignSchedule(campaignId)
  const { data: senderEmails } = useGetCampaignSenderEmails(bisonCampaignId)
  const { data: leadsData } = useGetCampaignLeads(bisonCampaignId, 1)

  // Mutations
  const launchMutation = useLaunchCampaign(bisonCampaignId, campaignId)
  const pauseMutation = usePauseCampaign(bisonCampaignId, campaignId)

  // Current campaign status
  const currentStatus = settings?.status || STATUS.DRAFT

  // Build checklist items
  const checklistItems: ChecklistItem[] = useMemo(() => {
    const stepCount = sequence?.steps?.length || 0
    const senderEmailCount = senderEmails?.count || 0
    const leadCount = leadsData?.count || 0
    const hasSchedule = !!schedule

    return [
      {
        id: 'steps',
        title: 'Sequence Steps',
        isComplete: stepCount > 0,
        description:
          stepCount > 0
            ? `${stepCount} step${stepCount !== 1 ? 's' : ''} configured`
            : 'No steps configured',
        targetTab: 'steps',
      },
      {
        id: 'schedule',
        title: 'Schedule',
        isComplete: hasSchedule,
        description: hasSchedule
          ? formatScheduleDescription(schedule)
          : 'No schedule configured',
        targetTab: 'schedule',
      },
      {
        id: 'sender-emails',
        title: 'Sender Emails',
        isComplete: senderEmailCount > 0,
        description:
          senderEmailCount > 0
            ? `${senderEmailCount} sender email${senderEmailCount !== 1 ? 's' : ''} attached`
            : 'No sender emails attached',
        targetTab: 'sender-emails',
      },
      {
        id: 'leads',
        title: 'Leads',
        isComplete: leadCount > 0,
        description:
          leadCount > 0
            ? `${leadCount} lead${leadCount !== 1 ? 's' : ''} synced`
            : 'No leads synced',
        targetTab: 'leads',
      },
    ]
  }, [sequence, schedule, senderEmails, leadsData])

  // Check if all items are complete
  const allComplete = checklistItems.every((item) => item.isComplete)

  // Handle launch
  const handleLaunch = async () => {
    try {
      await launchMutation.mutateAsync()
      toast.success('Campaign launched successfully!')
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail || 'Failed to launch campaign'
      toast.error(errorMessage)
    }
  }

  // Handle pause
  const handlePause = async () => {
    try {
      await pauseMutation.mutateAsync()
      toast.success('Campaign paused')
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail || 'Failed to pause campaign'
      toast.error(errorMessage)
    }
  }

  // Render active/launching/queued state
  if (
    currentStatus === STATUS.ACTIVE ||
    currentStatus === STATUS.LAUNCHING ||
    currentStatus === STATUS.QUEUED
  ) {
    return (
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-2 gap-8 flex-1 overflow-y-auto">
          {/* Left column - Status */}
          <div>
            <h3 className="text-sm font-semibold text-[#6E6E6E] uppercase tracking-wide mb-4">
              Campaign Status
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <StatusBadge status={currentStatus} />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">
                Your campaign is running
              </h4>
              <p className="text-gray-600 text-sm">
                Emails are being sent according to your schedule. You can pause
                the campaign at any time.
              </p>
            </div>
          </div>

          {/* Right column - Actions */}
          <div>
            <h3 className="text-sm font-semibold text-[#6E6E6E] uppercase tracking-wide mb-4">
              Campaign Actions
            </h3>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-sm text-gray-600 mb-4">
                Pausing the campaign will stop sending new emails. You can
                resume at any time without losing progress.
              </p>
              <FbButton
                onClick={handlePause}
                disabled={
                  pauseMutation.isPending || currentStatus === STATUS.LAUNCHING
                }
                className="w-full"
              >
                {pauseMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Pausing...
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Pause Campaign
                  </>
                )}
              </FbButton>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render draft/paused state with checklist
  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-2 gap-8 flex-1 overflow-y-auto">
        {/* Left column - Status */}
        <div>
          <h3 className="text-sm font-semibold text-[#6E6E6E] uppercase tracking-wide mb-4">
            Campaign Status
          </h3>
          <div
            className={`rounded-lg p-6 mb-6 ${
              currentStatus === STATUS.PAUSED
                ? 'bg-yellow-50 border border-yellow-200'
                : 'bg-gray-50 border border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <StatusBadge status={currentStatus} />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">
              {currentStatus === STATUS.PAUSED
                ? 'Campaign is paused'
                : 'Ready to configure'}
            </h4>
            <p className="text-gray-600 text-sm">
              {currentStatus === STATUS.PAUSED
                ? 'Your campaign is currently paused. Resume to continue sending emails.'
                : 'Complete the checklist to launch your campaign.'}
            </p>
          </div>

          {/* Launch/Resume button */}
          <FbButton
            onClick={handleLaunch}
            disabled={!allComplete || launchMutation.isPending}
            className="w-full"
          >
            {launchMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Launching...
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4 mr-2" />
                {currentStatus === STATUS.PAUSED
                  ? 'Resume Campaign'
                  : 'Launch Campaign'}
              </>
            )}
          </FbButton>

          {!allComplete && (
            <p className="text-sm text-gray-500 mt-2 text-center">
              Complete all checklist items to launch
            </p>
          )}
        </div>

        {/* Right column - Checklist */}
        <div>
          <h3 className="text-sm font-semibold text-[#6E6E6E] uppercase tracking-wide mb-4">
            Pre-launch Checklist
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {checklistItems.map((item) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                onNavigate={() => onNavigateToTab(item.targetTab)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to format schedule description
function formatScheduleDescription(schedule: any): string {
  if (!schedule) return ''

  const days = []
  if (schedule.monday) days.push('Mon')
  if (schedule.tuesday) days.push('Tue')
  if (schedule.wednesday) days.push('Wed')
  if (schedule.thursday) days.push('Thu')
  if (schedule.friday) days.push('Fri')
  if (schedule.saturday) days.push('Sat')
  if (schedule.sunday) days.push('Sun')

  const daysStr = days.join(', ') || 'No days'
  const timeStr = `${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`

  return `${daysStr}, ${timeStr}`
}

function formatTime(time: string): string {
  if (!time) return ''
  // Convert HH:MM:SS or HH:MM to readable format
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}
